/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { from, Subject } from 'rxjs';
import { concatMap, map, retry } from 'rxjs/operators';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import uniqueId from 'lodash/uniqueId';
import { Button, Checkbox, Icon, Spinner, StyledModal, Text } from 'sarsaparilla';
import PassInfoForm from 'ui-sitepass/src/components/PassInfoForm.jsx';
import {
    facilityByIdOrSlugSelectors,
    isLoaded,
    isLoading,
    passTypesSelectors,
} from 'ui-sitepass/src/reducers';
import {
    clearValidationErrors,
    fetchFacilityByIdOrSlug,
    fetchPassTypes,
    removeReservation,
} from 'ui-sitepass/src/actions';
import { ReservationDefaults } from 'ui-sitepass/src/props';
import { formatPrice } from 'ui-sitepass/src/utils';
import { isDailyPassType } from 'ui-sitepass/src/utils/date_time_utils';
import { getPrice } from 'ui-sitepass/src/api';
import {
    updateSitePassRemovedReservationIds,
    updateSitePassReservations,
} from '../../../actions/orderDetailsActions';
import SitePassSelection from './SitePassSelection';
import {
    ACADIA_FACILITY_ID,
    FACILITY_SITEPASS,
    HIDDEN_SITE_PASS_TYPES,
    isSnakeRiver,
} from '../../../constants';

const SitePass = forwardRef(
    (
        {
            facilityId,
            checkboxId,
            checkboxText,
            errorText,
            className,
            showPasses,
            isChecked,
            isInvalid,
            isRequired,
            visible,
            defaultStartDate,
            handleChange,
            userMustBuyAPass,
            newSitePassIntegrationEnabled,
            sitePassNotRequiredForDate,
        },
        ref
    ) => {
        const sitePassRef = useRef({
            sitePassReservations: [],
            sitePassTypes: [],
            priceSubject: new Subject(),
        });
        const dispatch = useDispatch();
        const [sitePassState, setSitePassState] = useState({
            showPasses,
            isInvalid,
            consentBoxChecked: !!isChecked,
            passCounts: {},
            reservationValidStatuses: {},
            sitePassesFeaturesModalStatus: 'HIDDEN',
        });
        const account = useSelector((state) => state?.auth?.account?.account);
        const sitePassReservations = useSelector(
            (state) => state?.timedEntry?.orderDetails?.sitePass?.reservations
        );

        const tourId = useSelector((state) => state?.timedEntry?.orderDetails?.tour_id);

        const tour = useSelector((state) =>
            state?.timedEntry?.tours?.tourList?.find(
                (tourItem) => tourItem?.tour_id === tourId
            )
        );
        const sitePassRemovedReservationIds = useSelector(
            (state) => state?.timedEntry?.orderDetails?.sitePass?.removedReservationIds
        );
        const ticketHolderDetails = useSelector(
            (state) => state?.timedEntry?.orderDetails?.ticketHolderDetails
        );
        const initialPassTypes = useSelector(passTypesSelectors?.getPassTypes);
        const sitePassFacility = useSelector(
            facilityByIdOrSlugSelectors?.getFacilityByIdOrSlug
        );
        const loading = useSelector((state) =>
            isLoading(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
        );
        const loaded = useSelector((state) =>
            isLoaded(state, 'reservations', 'facilityByIdOrSlug', 'passTypes')
        );

        const maxTotalGuestsReached = useMemo(() => {
            // We only allow to add one site pass
            return sitePassReservations?.length >= 1;
        }, [sitePassReservations?.length]);

        const facilitySitePassId = useMemo(() => {
            if (!isEmpty(tour?.site_pass_integration?.facility_id)) {
                return tour?.site_pass_integration?.facility_id;
            }

            return FACILITY_SITEPASS[facilityId];
        }, [facilityId, tour?.site_pass_integration?.facility_id]);

        const isSitePassIntegrationRequired = useMemo(() => {
            return tour?.site_pass_integration?.required;
        }, [tour?.site_pass_integration?.required]);

        const shouldShowSitePasses = useMemo(() => {
            return !!facilitySitePassId && !!visible;
        }, [facilitySitePassId, visible]);

        const passTypes = useMemo(() => {
            const newPassTypes = initialPassTypes.filter(
                (item) =>
                    !HIDDEN_SITE_PASS_TYPES?.some((passType) =>
                        item?.sku?.endsWith(passType)
                    ) && !item?.features?.is_field_sales
            );
            sitePassRef.current.sitePassTypes = newPassTypes;
            return newPassTypes;
        }, [initialPassTypes]);

        const shouldSetDefaultDate = useMemo(() => {
            return facilityId !== ACADIA_FACILITY_ID;
        }, [facilityId]);

        const showNonResidentFees = useMemo(() => {
            return passTypes?.some((pt) => pt?.features?.non_resident_fee);
        }, [passTypes]);

        const getReservationPrice = useCallback(
            ({ id, pass_type_id, features, pass_info: { start_date: startDate } }) => {
                const currentPassType = sitePassRef.current.sitePassTypes?.find(
                    (item) => item.id === pass_type_id
                );
                if (isDailyPassType(currentPassType?.duration) && isEmpty(startDate)) {
                    return {};
                }
                return getPrice({
                    pass_type_id,
                    features: {
                        ...features,
                        'start-date': startDate,
                    },
                }).then(({ data }) => ({ data: { ...data, id } }));
            },
            []
        );

        const handleSetSitePassesFeaturesModalStatus = useCallback(
            (status = 'HIDDEN') =>
                () => {
                    setSitePassState((prevState) => ({
                        ...prevState,
                        sitePassesFeaturesModalStatus: status,
                    }));
                },
            []
        );

        const handleReservationChange = useCallback(
            (reservation, reservationIsValid) => {
                const reservations = cloneDeep(sitePassReservations);
                sitePassRef.current.sitePassReservations = reservations;
                const reservationValidStatuses = cloneDeep(
                    sitePassState?.reservationValidStatuses
                );
                const index = reservations.findIndex(({ id }) => id === reservation.id);

                let oldFeatures = cloneDeep(reservation.pass_info.features);
                const newFeatures = cloneDeep(reservation.pass_info.features);

                if (index > -1) {
                    oldFeatures = reservations[index].pass_info.features;
                    reservations[index] = reservation;
                    if (!isEqual(oldFeatures, newFeatures)) {
                        sitePassRef.current.priceSubject.next(reservation);
                    }
                }

                setSitePassState((prevState) => ({
                    ...prevState,
                    reservationValidStatuses: {
                        ...reservationValidStatuses,
                        [reservation?.id]: reservationIsValid,
                    },
                }));

                sitePassRef.current.sitePassReservations = reservations;
                dispatch(updateSitePassReservations(reservations));
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [sitePassReservations, sitePassState?.reservationValidStatuses]
        );

        const handleReservationChangeDebounced = useMemo(
            () => debounce(handleReservationChange, 500),
            [handleReservationChange]
        );

        useEffect(() => {
            sitePassRef.current.priceSubject
                .asObservable()
                .pipe(
                    concatMap((reservation) => from(getReservationPrice(reservation))),
                    retry(5),
                    map(({ data }) => data)
                )
                .subscribe((pricing) => {
                    const reservations = cloneDeep(
                        sitePassRef.current.sitePassReservations
                    );
                    const { id, fees, price, total } = pricing;
                    const index = reservations.findIndex(({ id: rId }) => rId === id);
                    if (index > -1) {
                        Object.assign(reservations[index], {
                            has_price: true,
                            fees,
                            price,
                            total,
                        });

                        if (
                            !isEqual(
                                sitePassRef.current.sitePassReservations,
                                reservations
                            )
                        ) {
                            sitePassRef.current.sitePassReservations = reservations;
                            dispatch(updateSitePassReservations(reservations));
                        }
                    }
                });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            if (!sitePassState?.consentBoxChecked) {
                setSitePassState((prevState) => ({ ...prevState, showPasses: false }));
            }
        }, [sitePassState?.consentBoxChecked]);

        useEffect(() => {
            setSitePassState((prevState) => ({
                ...prevState,
                consentBoxChecked: !!isChecked,
            }));
        }, [isChecked]);

        useEffect(() => {
            if (facilitySitePassId && sitePassState?.showPasses) {
                dispatch(fetchFacilityByIdOrSlug(facilitySitePassId));
                dispatch(fetchPassTypes(facilitySitePassId));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [facilitySitePassId, sitePassState?.showPasses]);

        // User must buy a pass but hasn't actually added one to the reservation yet.
        const mustAddPass = userMustBuyAPass && isEmpty(sitePassReservations);

        // This method is used externally for form validation. See the form validation example in the docs
        // eslint-disable-next-line no-unused-vars
        const validate = useCallback(() => {
            const validationObject = {
                isInvalid: false,
                errorText: null,
                id: checkboxId,
            };

            if (shouldShowSitePasses) {
                let isInvalidValue = false;

                if (!sitePassState?.consentBoxChecked || mustAddPass) {
                    validationObject.isInvalid = true;
                    validationObject.errorText = errorText;
                    isInvalidValue = true;
                }

                // if (isSitePassIntegrationRequired && isEmpty(sitePassState.passCounts)) {
                //     validationObject.isInvalid = true;
                //     validationObject.errorText = errorText;
                //     isInvalidValue = true;
                // }

                setSitePassState((prevState) => ({
                    ...prevState,
                    isInvalid: isInvalidValue,
                }));
            }

            return validationObject;
        }, [
            checkboxId,
            errorText,
            shouldShowSitePasses,
            sitePassState?.consentBoxChecked,
            sitePassState?.showPasses,
            mustAddPass,
        ]);

        const handlePassCountChange = useCallback(
            (passTypeId, count) => {
                const goingUp = count > (sitePassState?.passCounts[passTypeId] || 0);

                let reservations = cloneDeep(sitePassReservations);
                const reservationValidStatuses = cloneDeep(
                    sitePassState?.reservationValidStatuses
                );
                const passInfo = {
                    pass_info: {
                        ...ReservationDefaults.pass_info,
                    },
                };

                if (!isNil(account)) {
                    passInfo.pass_info.holder_info = {
                        first_name: account?.first_name,
                        last_name: account?.last_name,
                        email: account?.email,
                    };
                }

                if (!passInfo?.pass_info?.holder_info?.first_name) {
                    passInfo.pass_info.holder_info.first_name =
                        ticketHolderDetails?.firstName;
                }

                if (!passInfo?.pass_info?.holder_info?.last_name) {
                    passInfo.pass_info.holder_info.last_name =
                        ticketHolderDetails?.lastName;
                }

                if (!passInfo?.pass_info?.holder_info?.email) {
                    passInfo.pass_info.holder_info.email = ticketHolderDetails?.email;
                }

                if (goingUp) {
                    const newReservation = {
                        ...ReservationDefaults,
                        id: uniqueId(),
                        is_pending: true,
                        facility_id: facilitySitePassId,
                        pass_type_id: passTypeId,
                        ...passInfo,
                    };

                    const defaultDate =
                        shouldSetDefaultDate && defaultStartDate
                            ? moment(defaultStartDate)
                            : moment();
                    const date = defaultDate?.startOf('day')?.format();
                    newReservation.pass_info.start_date = date;
                    newReservation.pass_info.features['start-date'] = date;
                    const currentPassType = passTypes?.find(
                        (item) => item.id === passTypeId
                    );

                    newReservation.pass_info.duration = currentPassType?.duration;
                    if (
                        !shouldSetDefaultDate &&
                        currentPassType &&
                        isDailyPassType(currentPassType?.duration)
                    ) {
                        newReservation.pass_info.start_date = '';
                        newReservation.pass_info.features['start-date'] = '';
                    }
                    reservations = reservations?.concat(newReservation);
                } else {
                    let removedReservationIds = [...sitePassRemovedReservationIds];

                    reservations = reservations?.concat()?.filter((reservation) => {
                        const { pass_type_id: ptId } = reservation;
                        if (ptId === passTypeId) {
                            if (reservation && !reservation?.is_pending) {
                                removedReservationIds = removedReservationIds?.concat(
                                    reservation?.id
                                );
                            }

                            reservationValidStatuses[reservation?.id] = true;
                        }

                        return ptId !== passTypeId;
                    });

                    dispatch(updateSitePassRemovedReservationIds(removedReservationIds));
                }

                setSitePassState((prevState) => ({
                    ...prevState,
                    passCounts: {
                        ...prevState.passCounts,
                        [passTypeId]: count,
                    },
                    reservationValidStatuses,
                }));
                sitePassRef.current.sitePassReservations = reservations;
                dispatch(updateSitePassReservations(reservations));
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                sitePassState?.passCounts,
                sitePassState?.reservationValidStatuses,
                sitePassReservations,
                account,
                facilitySitePassId,
                passTypes,
            ]
        );

        const handleSelectPass = useCallback(
            (passTypeId) => {
                let reservations = cloneDeep(sitePassReservations);
                const passInfo = {
                    pass_info: {
                        ...ReservationDefaults.pass_info,
                    },
                };

                if (!isNil(account)) {
                    passInfo.pass_info.holder_info = {
                        first_name: account?.first_name,
                        last_name: account?.last_name,
                        email: account?.email,
                    };
                }

                if (!passInfo?.pass_info?.holder_info?.first_name) {
                    passInfo.pass_info.holder_info.first_name =
                        ticketHolderDetails?.firstName;
                }

                if (!passInfo?.pass_info?.holder_info?.last_name) {
                    passInfo.pass_info.holder_info.last_name =
                        ticketHolderDetails?.lastName;
                }

                if (!passInfo?.pass_info?.holder_info?.email) {
                    passInfo.pass_info.holder_info.email = ticketHolderDetails?.email;
                }

                const newReservation = {
                    ...ReservationDefaults,
                    id: uniqueId(),
                    is_pending: true,
                    facility_id: facilitySitePassId,
                    pass_type_id: passTypeId,
                    ...passInfo,
                };

                const defaultDate =
                    shouldSetDefaultDate && defaultStartDate
                        ? moment(defaultStartDate)
                        : moment();
                const date = defaultDate?.startOf('day')?.format();
                newReservation.pass_info.start_date = date;
                newReservation.pass_info.features['start-date'] = date;
                const currentPassType = passTypes?.find((item) => item.id === passTypeId);

                newReservation.pass_info.duration = currentPassType?.duration;
                if (
                    !shouldSetDefaultDate &&
                    currentPassType &&
                    isDailyPassType(currentPassType?.duration)
                ) {
                    newReservation.pass_info.start_date = '';
                    newReservation.pass_info.features['start-date'] = '';
                }
                reservations = [newReservation];
                sitePassRef.current.sitePassReservations = reservations;
                setSitePassState((prevState) => ({
                    ...prevState,
                    selectedPassTypeId: passTypeId,
                }));
                dispatch(updateSitePassReservations(reservations));
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                sitePassReservations,
                account,
                facilitySitePassId,
                shouldSetDefaultDate,
                defaultStartDate,
                passTypes,
                ticketHolderDetails?.firstName,
                ticketHolderDetails?.lastName,
                ticketHolderDetails?.email,
            ]
        );

        const handleRemovePass = useCallback(
            (passTypeId) => {
                let reservations = cloneDeep(sitePassReservations);
                const passInfo = {
                    pass_info: {
                        ...ReservationDefaults.pass_info,
                    },
                };

                if (!isNil(account)) {
                    passInfo.pass_info.holder_info = {
                        first_name: account?.first_name,
                        last_name: account?.last_name,
                        email: account?.email,
                    };
                }

                if (!passInfo?.pass_info?.holder_info?.first_name) {
                    passInfo.pass_info.holder_info.first_name =
                        ticketHolderDetails?.firstName;
                }

                if (!passInfo?.pass_info?.holder_info?.last_name) {
                    passInfo.pass_info.holder_info.last_name =
                        ticketHolderDetails?.lastName;
                }

                if (!passInfo?.pass_info?.holder_info?.email) {
                    passInfo.pass_info.holder_info.email = ticketHolderDetails?.email;
                }

                let removedReservationIds = [...sitePassRemovedReservationIds];

                reservations = reservations?.concat()?.filter((reservation) => {
                    const { pass_type_id: ptId } = reservation;
                    if (ptId === passTypeId) {
                        if (reservation && !reservation?.is_pending) {
                            removedReservationIds = removedReservationIds?.concat(
                                reservation?.id
                            );
                        }
                    }

                    return ptId !== passTypeId;
                });

                dispatch(updateSitePassRemovedReservationIds(removedReservationIds));
                sitePassRef.current.sitePassReservations = reservations;
                setSitePassState((prevState) => ({
                    ...prevState,
                    selectedPassTypeId: null,
                }));
                dispatch(updateSitePassReservations(reservations));
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [
                sitePassReservations,
                account,
                facilitySitePassId,
                shouldSetDefaultDate,
                defaultStartDate,
                passTypes,
                ticketHolderDetails?.firstName,
                ticketHolderDetails?.lastName,
                ticketHolderDetails?.email,
            ]
        );

        const handleRemoveReservation = useCallback(
            async (id) => {
                let reservations = cloneDeep(sitePassReservations);
                const reservationValidStatuses = cloneDeep(
                    sitePassState?.reservationValidStatuses
                );

                const index = reservations.findIndex(({ id: rid }) => id === rid);
                if (index > -1) {
                    // Remove any validation errors
                    dispatch(clearValidationErrors(index));
                    // Remove on the server
                    try {
                        await dispatch(removeReservation(id));
                        const res = reservations[index];
                        const passTypeId = res?.pass_type_id;
                        reservationValidStatuses[res.id] = true;
                        delete reservations[index];
                        reservations = compact(reservations);
                        setSitePassState((prevState) => ({
                            ...prevState,
                            passCounts: {
                                ...prevState.passCounts,
                                [passTypeId]: prevState.passCounts[passTypeId] - 1,
                            },
                            reservationValidStatuses,
                            selectedPassTypeId: null,
                        }));

                        sitePassRef.current.sitePassReservations = reservations;
                        dispatch(updateSitePassReservations(reservations));
                    } catch (e) {
                        // No-op. Errors will propagate through props.errors
                    }
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [sitePassState?.reservationValidStatuses, sitePassReservations]
        );

        useImperativeHandle(ref, () => ({
            validate,
        }));

        const toggleCheckBox = () => {
            // toggle status of the checkbox
            handleChange();
            setSitePassState((prevState) => {
                const consentBoxChecked = !prevState.consentBoxChecked;
                const newState = { consentBoxChecked };
                if (!consentBoxChecked) {
                    newState.passCounts = {};
                }
                return {
                    ...prevState,
                    ...newState,
                };
            });
        };

        const handleAddSitePass = () => {
            setSitePassState((prevState) => ({ ...prevState, showPasses: true }));
        };

        const handlePassInfoChange =
            (reservationId) => (passTypeId, passInfo, isValid) => {
                const reservation = sitePassReservations.find(
                    ({ id }) => id === reservationId
                );
                if (reservation) {
                    // Default "discount" feature.
                    const discount = {};

                    // Format "accepted" feature.
                    const passType = passTypes.find(
                        ({ id }) => id === reservation?.pass_type_id
                    );
                    const accepted = {};
                    if (passType?.features?.accepted) {
                        accepted.accepted = passType?.features?.accepted?.reduce(
                            (memo, key) => ({
                                ...memo,
                                [key]: true,
                            }),
                            {}
                        );
                    }

                    // Build updated reservation.
                    const updatedReservation = {
                        ...reservation,
                        pass_info: passInfo,

                        // New API updates:
                        pass_type_id: passTypeId || reservation?.pass_type_id,
                        holder_info: passInfo?.holder_info,
                        features: {
                            discount,
                            ...passInfo?.features,
                            ...accepted,
                        },
                    };

                    handleReservationChangeDebounced(updatedReservation, isValid);
                }
            };

        const renderLabel = () => {
            return (
                <>
                    {checkboxText}
                    {isRequired && (
                        <abbr className="rec-label-required-indicator">
                            <span aria-hidden="true">*</span>
                            <span className="rec-sr-only"> (Required) </span>
                        </abbr>
                    )}
                </>
            );
        };

        const renderAddSitePasButton = () => {
            if (sitePassState?.consentBoxChecked && !sitePassState?.showPasses) {
                return (
                    <div className="tic-site-pass__button-container">
                        <Button
                            iconBeforeElement={<Icon iconName="add-circle-outline" />}
                            clickTagAction={`Location ID - ${facilityId}`}
                            clickTagCategory="Timed Entry"
                            clickTagLabel="Pass Upsell Promo - Add a Site Pass"
                            onClick={handleAddSitePass}
                        >
                            Add a Site Pass
                        </Button>
                    </div>
                );
            }

            return null;
        };

        const renderDateInfoContent = () => {
            const dateDescription = isSnakeRiver(facilityId)
                ? `Make sure one of your site pass dates will be valid when your reservation is!`
                : `Make sure one of your site pass dates will be valid when your vehicle reservation is!`;

            const defaultStartDateText = defaultStartDate
                ? isSnakeRiver(facilityId)
                    ? `Reservation Date: ${moment(defaultStartDate)?.format(
                          'MM/DD/YYYY'
                      )}`
                    : `Vehicle Reservation Date: ${moment(defaultStartDate)?.format(
                          'MM/DD/YYYY'
                      )}`
                : '';

            return (
                <>
                    <Text className="m-0">{dateDescription}</Text>
                    {defaultStartDateText && (
                        <Text className="m-0" fontWeight="semibold">
                            {defaultStartDateText}
                        </Text>
                    )}
                </>
            );
        };

        const renderPassInfoForms = () => {
            if (!sitePassReservations?.length) {
                return null;
            }

            return sitePassReservations?.map((reservation, index) => {
                const passType = passTypes.find(
                    ({ id }) => id === reservation.pass_type_id
                );
                if (!passType) {
                    return null;
                }

                const number = index + 1;
                const price = formatPrice(reservation?.price);

                return (
                    <div
                        className="rec-internal-reservation-details-parkpass mt-4"
                        key={`pass-info-form-${reservation.id}`}
                    >
                        <PassInfoForm
                            submitAttempted={false}
                            isRequired={isSitePassIntegrationRequired}
                            facility={sitePassFacility}
                            number={number}
                            passInfo={reservation?.pass_info}
                            passType={passType}
                            price={price}
                            renderRemove
                            isPending={reservation?.is_pending}
                            reservationId={reservation?.id}
                            renderDateInfoContent={renderDateInfoContent}
                            onChange={handlePassInfoChange(reservation?.id)}
                            onRemove={handleRemoveReservation}
                            showNonResidentFees={showNonResidentFees}
                        />
                    </div>
                );
            });
        };

        const renderSitePassesContent = () => {
            if (loading.passTypes) {
                return <Spinner size="lg" />;
            }

            if (loaded && sitePassState?.showPasses) {
                return (
                    <>
                        <SitePassSelection
                            selectedPassTypeId={sitePassState?.selectedPassTypeId}
                            passTypes={passTypes}
                            passCounts={sitePassState?.passCounts}
                            maxTotalGuestsReached={maxTotalGuestsReached}
                            onChange={handlePassCountChange}
                            onSelectPass={handleSelectPass}
                            onRemovePass={handleRemovePass}
                            facilityId={tour?.site_pass_integration?.facility_id}
                            reservations={sitePassReservations}
                        />
                        <div className="mt-2">
                            <Button
                                appearance="link"
                                iconAfterElement={<Icon iconName="info" />}
                                size="md"
                                onClick={handleSetSitePassesFeaturesModalStatus(
                                    'VISIBLE'
                                )}
                            >
                                Site Passes Features
                            </Button>
                        </div>
                        {renderPassInfoForms()}
                    </>
                );
            }

            return null;
        };

        const renderSitePassesFeaturesModal = () => (
            <StyledModal
                size="md"
                heading="Site Passes Features"
                isOpen={sitePassState?.sitePassesFeaturesModalStatus === 'VISIBLE'}
                onRequestClose={handleSetSitePassesFeaturesModalStatus('HIDDEN')}
            >
                <ul className="tic-site-pass__features-list">
                    <li>
                        <Text>Access to only a single federal recreation site.</Text>
                    </li>
                    <li>
                        <Text>
                            Price varies based on location and pass type (daily, weekly,
                            seasonal, etc.)
                        </Text>
                    </li>
                    <li>
                        <Text>
                            {`Digital pass is available through ${process.env.SITE_NAME} for instant download and use on a phone or tablet.`}
                        </Text>
                    </li>
                    <li>
                        <Text>
                            Non-refundable, non-transferable, and cannot be replaced if
                            lost or stolen.
                        </Text>
                    </li>
                    <li>
                        <Text>
                            Proceeds from pass sales are used to improved and enhance
                            visitor services.
                        </Text>
                    </li>
                </ul>
            </StyledModal>
        );

        if (!shouldShowSitePasses) {
            return null;
        }

        const renderSitePassInformationV2 = () => {
            if (sitePassNotRequiredForDate) {
                return null;
            }
            if (newSitePassIntegrationEnabled) {
                if (userMustBuyAPass) {
                    //Snake River requires specific message
                    if (tour?.site_pass_integration?.facility_id === '74396') {
                        return (
                            <>
                                <Text className="mb-2">
                                    Please purchase a Site Pass for each member aged 16+
                                    in your group per day, and indicate how many children
                                    in the group. If you purchase a second launch time
                                    reservation for the same day, you will not need to pay
                                    for additional site passes. To buy a Site Pass now,
                                    click &quot;Add a Site Pass.&quot;
                                </Text>
                                {renderAddSitePasButton()}
                            </>
                        );
                    }
                } else {
                    return null;
                }
            }
            //For other facilities just return a general message
            return (
                <>
                    <Text size="lg" fontWeight="bold" className="mt-1">
                        Options
                    </Text>
                    <Text className="m-2">
                        <b>Do not purchase a Site Pass</b> if anyone in your party already
                        has an Interagency Senior, Access, Military, 4th Grade or other
                        valid pass or you plan to purchase a pass upon arrival.{' '}
                        <Button
                            appearance="link"
                            size="sm"
                            href={`${process.env.SITE_ROOT}/pass?section_id=interagency-annual-pass`}
                            target="_blank"
                        >
                            Learn more about pass options here
                        </Button>
                    </Text>
                    <Text className="m-2">
                        <b>Purchase a Site Pass now</b> for my trip. I don&apos;t have an
                        annual, lifetime or local pass for this location. To buy a Site
                        Pass now, click on &quot;Add a Site Pass.&quot;
                    </Text>
                    {renderAddSitePasButton()}
                </>
            );
        };

        const showInlineError =
            sitePassState?.isInvalid &&
            (!sitePassState?.consentBoxChecked || mustAddPass);

        return (
            <section className={cx('tic-site-pass', className)} ref={ref}>
                <div
                    className={cx('rec-form-check-wrap', {
                        'is-invalid': showInlineError,
                    })}
                >
                    <Checkbox
                        id={checkboxId}
                        aria-describedby={
                            sitePassState?.isInvalid ? `${checkboxId}-error` : null
                        }
                        label={renderLabel()}
                        isRequired
                        isChecked={sitePassState?.consentBoxChecked}
                        value={checkboxId}
                        onChange={toggleCheckBox}
                    />
                    {renderSitePassInformationV2()}
                </div>
                {showInlineError && (
                    <div id={`${checkboxId}-error`} className="sarsa-field-error-message">
                        <span className="req" aria-hidden="true">
                            *
                        </span>
                        {errorText}
                    </div>
                )}
                {!sitePassNotRequiredForDate && (
                    <div className={sitePassState?.showPasses ? 'mt-4' : undefined}>
                        {renderSitePassesContent()}
                    </div>
                )}
                {renderSitePassesFeaturesModal()}
            </section>
        );
    }
);

SitePass.propTypes = {
    facilityId: PropTypes.string,
    checkboxId: PropTypes.string,
    checkboxText: PropTypes.string,
    errorText: PropTypes.string,
    className: PropTypes.string,
    showPasses: PropTypes.bool,
    isChecked: PropTypes.bool,
    isInvalid: PropTypes.bool,
    isRequired: PropTypes.bool,
    visible: PropTypes.bool,
    defaultStartDate: PropTypes.object,
    handleChange: PropTypes.func,
    availableSitePass: PropTypes.string,
    newSitePassIntegrationEnabled: PropTypes.bool,
    userMustBuyAPass: PropTypes.bool,
    sitePassNotRequiredForDate: PropTypes.bool,
};

SitePass.defaultProps = {
    checkboxId: 'site-pass-checkbox',
    checkboxText: 'I understand a pass is also required at this location.',
    errorText: 'Please read and agree to the important Site Pass information.',
    className: '',
    showPasses: false,
    isChecked: false,
    isInvalid: false,
    isRequired: true,
    visible: false,
    userMustBuyAPass: false,
    newSitePassIntegrationEnabled: false,
    sitePassNotRequiredForDate: false,
    defaultStartDate: null,
    availableSitePass: '',
    handleChange: () => {},
};

export default SitePass;
